/* eslint-disable max-len */
/* eslint-disable no-alert */
/* eslint-disable no-restricted-syntax */
import { SECOND_IN_MILLISECONDS } from "@daytrip/constants";
import { API_SIGN_IN_TO_CI_AS_CUSTOMER_URL } from "@daytrip/legacy-config";
import {
    AssignationStatus,
    OfferType,
    OrderStatus,
    OrderType,
    PassengerType,
    VehicleType,
    VehicleTypes,
} from "@daytrip/legacy-enums";
import type { VehicleTypeCoefficient } from "@daytrip/legacy-models";
import type { Location } from "@daytrip/legacy-models";
import type { SimpleCountry } from "@daytrip/legacy-models";
import { Order, OrderPriceCalculationValidationOptions } from "@daytrip/legacy-models";
import { PriceCalculator } from "@daytrip/legacy-models";
import { Assignation } from "@daytrip/legacy-models";
import { CustomerFeedback } from "@daytrip/legacy-models";
import type { OrderDataForOrderPage } from "@daytrip/legacy-utils";
import { getPoolDepartureTimeDetails } from "@daytrip/legacy-utils";
import { validateWithoutLogging } from "@daytrip/legacy-utils";
import { transformBookingToUTC, isUndefinedOrNull, isNotUndefinedOrNull } from "@daytrip/utils";
import autobind from "autobind-decorator";
import { plainToClass } from "class-transformer";
import { action, computed, observable } from "mobx";
import type { LocationDescriptor } from "mobx-react-router";

import { globalManagementLogger } from "../../global-logger";
import { CustomerFeedbackOperator } from "../../operators/CustomerFeedbackOperator";
import { OrderOperator } from "../../operators/OrderOperator";
import { PageStore } from "../../stores/PageStore";
import { trackOrderEditEvent } from "../../utils/analytics/trackOrderEditEvent";
import { FetchDataStatus } from "../../utils/FetchDataStatus";
import { validatePassengersPhoneNumbers } from "../../utils/validatePassengersPhoneNumbers";

import type { OrderPageRouter } from "./OrderPageRouter";
import { checkLocationsDifference } from "./utils/checkLocationsDifference";
import { checkAndConfirmVehicleTypePriceFeeDifference } from "./utils/checkVehicleTypePriceFeeDifference";
import { detectOrderChanges } from "./utils/detectOrderChanges";
import {
    calculateHoursSincePurchase,
    calculateHoursUntilDeparture,
    mapAirportDirection,
} from "./utils/orderEditAnalyticsHelpers";
import { apiTrpcClient } from "@/trpc/api";

const INVALID_ASSIGNATION_STATUSES = [AssignationStatus.Cancelled, AssignationStatus.Declined];

@autobind
export class OrderPageStore extends PageStore<OrderPageRouter, {}> {
    @observable
    public orderOperator: any;
    // ts_fix_bullshit Fix OrderOperator type and use here
    // public orderOperator: OrderOperator;

    @action
    public async onFetchData() {
        await this.fetchOrder();
    }

    @action
    private async fetchOrder() {
        const orderOperatorModules = { ...this.modules, routingStore: this.pageRouter.routerStore };

        this.orderOperator = new OrderOperator({
            modelConstructor: Order,
            modules: orderOperatorModules,
            beforeEdit: async () => {
                if (isUndefinedOrNull(this.orderOperator.editedModel)) {
                    const { originLocation, destinationLocation } = this.orderOperator;
                    if (this.orderOperator.originLocationSearchOption == null && originLocation != null) {
                        this.orderOperator.originLocationSearchOption = {
                            value: originLocation._id,
                            label: originLocation.name,
                        };
                    }

                    if (this.orderOperator.destinationLocationSearchOption == null && destinationLocation != null) {
                        this.orderOperator.destinationLocationSearchOption = {
                            value: destinationLocation._id,
                            label: destinationLocation.name,
                        };
                    }

                    this.orderOperator.contentLocationOperators = this.orderOperator.contentLocationOperators.map(
                        (clo) => {
                            clo.data.countries = this.orderOperator.data.simpleCountries as Array<SimpleCountry>;
                            clo.data.location = this.orderOperator.data.locations.find(
                                (l) => l._id === clo.m.locationId,
                            ) as Location;

                            return clo;
                        },
                    );

                    this.orderOperator.customLocationOperators = this.orderOperator.customLocationOperators.map(
                        (clo) => {
                            clo.data.countries = this.orderOperator.data.simpleCountries as Array<SimpleCountry>;

                            return clo;
                        },
                    );

                    this.orderOperator.setFullyFetched();
                }
            },
            data: {
                locations: [],
                assignations: [],
                subsidies: [],
                compensations: [],
                penalties: [],
                discounts: [],
            },
            onFetchData: async () => {
                await Promise.all([
                    this.loadOrderWithDependentData(this.orderOperator),
                    this.loadSimpleCountries(this.orderOperator),
                    this.loadRouterUser(this.orderOperator),
                    this.loadRegions(this.orderOperator),
                    this.loadHostAgencies(this.orderOperator),
                ]);

                return FetchDataStatus.Success;
            },
            beforeSave: async () => {
                const { orderOperator } = this;

                if (orderOperator.newOrder === false) {
                    const originalOrder = await this.rpcClient.order.retrieveOrder(orderOperator.m._id);

                    await checkLocationsDifference(
                        orderOperator,
                        originalOrder,
                        this.rpcClient.content.retrieveSimpleLocations,
                    );
                    checkAndConfirmVehicleTypePriceFeeDifference(orderOperator);
                }

                await this.orderOperator.validate();
            },
            onSave: async (model: Order) => {
                const { orderOperator } = this;

                let orderWasNew = false;

                const originLocation = await this.rpcClient.content.retrieveLocation(model.originLocationId);
                const departureAtUtc = transformBookingToUTC(model.departureAt, originLocation.timezone);

                const now = new Date();
                if (departureAtUtc < now) {
                    alert(
                        [
                            "Please consider that order departure date is in the past.",
                            `UTC: ${departureAtUtc.toJSON()}`,
                            `Order is in timezone: ${originLocation.timezone} (${model.departureAt.toJSON()})`,
                        ].join("\n"),
                    );
                }

                const phoneNumberValidationResult = validatePassengersPhoneNumbers(orderOperator);

                if (phoneNumberValidationResult) {
                    alert(phoneNumberValidationResult.alertMessage);
                    // if order has API partner (API partner do not support strict phone validation),
                    // allow to save with invalid phone number so CS team can edit such orders
                    if (phoneNumberValidationResult.shouldNotSaveOrderWithInvalidPhones) {
                        return;
                    }
                }

                if (orderOperator && orderOperator.newOrder === false) {
                    const editedOrder = plainToClass(Order, model);

                    if (editedOrder) {
                        try {
                            // Retrieve original order before update for change detection
                            const originalOrder = await this.rpcClient.order.retrieveOrder(editedOrder._id);

                            // Update order
                            await this.rpcClient.order.updateOrder(editedOrder._id, editedOrder);

                            // Track order edit in Amplitude
                            const changes = detectOrderChanges(originalOrder, editedOrder);

                            if (changes.hasChanges) {
                                trackOrderEditEvent({
                                    orderId: editedOrder._id,
                                    bundleId: editedOrder.bundleId,
                                    originalCustomerId: editedOrder.userId,

                                    // Changes
                                    changeTypes: changes.changeTypes,
                                    changeCount: changes.changeCount,

                                    // Context
                                    orderStatus: OrderStatus[editedOrder.status],
                                    typeOfCustomer: editedOrder.travelAgentId ? "TravelAgent" : "B2C",
                                    tripType: this.getTripType(editedOrder),
                                    hoursUntilDeparture: calculateHoursUntilDeparture(departureAtUtc),
                                    airportDirection: mapAirportDirection(editedOrder.airportTransferDirection),
                                    priceChangeAmount: changes.priceChangeAmount,

                                    // Versioning
                                    versionBefore: originalOrder.version || 0,
                                    versionAfter: (originalOrder.version || 0) + 1,

                                    // Timing
                                    hoursSincePurchase: calculateHoursSincePurchase(editedOrder.createdAt),
                                });
                            }

                            this.orderOperator.edit((order) => {
                                if (order.version != null) {
                                    order.version += 1;
                                } else {
                                    order.version = 0;
                                }
                            });
                            this.orderOperator.orderLastSaved = new Date();

                            await validateWithoutLogging(Order, editedOrder, OrderPriceCalculationValidationOptions);

                            // Update assignation price if order prices have changed
                            const currentTotalPrice = PriceCalculator.calculateOrderPrice(
                                editedOrder,
                                orderOperator.orderDiscounts,
                            );
                            if (orderOperator.model.price !== currentTotalPrice) {
                                const assignations = await this.rpcClient.assignation.retrieveAssignationsForOrderId(
                                    orderOperator.model._id,
                                );
                                const activeAssignations = assignations.filter(
                                    (assignation) => !INVALID_ASSIGNATION_STATUSES.includes(assignation.status),
                                );
                                activeAssignations.forEach(async (assignation) => {
                                    const vehicleOrder = { ...editedOrder };
                                    vehicleOrder.vehicles = [assignation.vehicleType];
                                    await validateWithoutLogging(
                                        Order,
                                        vehicleOrder as Order,
                                        OrderPriceCalculationValidationOptions,
                                    );
                                    assignation.price = PriceCalculator.calculateOrderPrice(
                                        vehicleOrder as Order,
                                        orderOperator.orderDiscounts,
                                    );
                                    assignation.fee = PriceCalculator.calculateOrderFee(
                                        vehicleOrder as Order,
                                        orderOperator.orderDiscounts,
                                    );
                                    await this.rpcClient.assignation.updateAssignation(assignation._id, assignation);
                                });
                            }
                        } catch (e: any) {
                            alert(
                                `Unable to update order. It could be the result of someone updated this order meanwhile. Please, reload the page and try again.\n\n${e}`,
                            );
                            globalManagementLogger.error(e);
                        }
                    }
                } else if (this.orderOperator.newOrder === true) {
                    orderWasNew = true;
                    // return coefficients = 1 if no blacklisted date price coefficient
                    const priceCoefficient = await this.retrieveBlacklistedDatePriceCoefficient(model);

                    model.vehicleTypesPricesFees.forEach((vtpf, i) => {
                        const vehicleCoefficient = priceCoefficient.vehicleCoefficients.find(
                            (coefficient) => coefficient.vehicleType === vtpf.vehicleType,
                        );
                        if (vehicleCoefficient) {
                            model.vehicleTypesPricesFees[i].price = Math.round(
                                model.vehicleTypesPricesFees[i].price / vehicleCoefficient.priceAdjustmentCoefficient,
                            );
                            model.vehicleTypesPricesFees[i].fee = Math.round(
                                model.vehicleTypesPricesFees[i].fee / vehicleCoefficient.feeAdjustmentCoefficient,
                            );
                        }
                    });
                    model.travelAgentId = orderOperator.travelAgentId;
                    model.hostAgencyId = orderOperator.hostAgencyId;
                    const savedOrderId = await this.rpcClient.order.createOrder(model);

                    orderOperator.orderLastSaved = new Date();
                    orderOperator.newOrder = false;
                    orderOperator.m._id = savedOrderId;
                }

                await orderOperator.retrieveLatestPendingChange();

                if (orderOperator.vehicleOperators) {
                    for (const vehicleOperator of orderOperator.vehicleOperators) {
                        const { assignationOperator } = vehicleOperator;

                        if (isNotUndefinedOrNull(assignationOperator?.model) && assignationOperator.isSaved !== true) {
                            assignationOperator.edit((a) => {
                                a.orderId = orderOperator.m._id;
                            });
                            await assignationOperator.save();
                            orderOperator.data.assignations.push(assignationOperator.model);
                            assignationOperator.isSaved = true;

                            if (isNotUndefinedOrNull(assignationOperator.customerFeedbackOperator)) {
                                assignationOperator.customerFeedbackOperator.edit();
                                await assignationOperator.customerFeedbackOperator.save();
                            }
                        }
                    }
                }

                if (orderOperator.paymentRequestOperators.length > 0) {
                    orderOperator.paymentRequestOperators.forEach(async (pro) => {
                        pro.edit((pr) => {
                            pr.orderId = orderOperator.m._id;
                        });
                        await pro.save();
                    });
                }

                if (orderOperator.paymentOperators.length > 0) {
                    orderOperator.paymentOperators.forEach(async (po) => {
                        po.edit((p) => {
                            p.orderId = orderOperator.m._id;
                        });
                        await po.save();
                    });
                }

                if (orderWasNew) {
                    this.orderOperator.modules.routingStore.push({
                        pathname: "order",
                        query: { orderId: orderOperator.m._id },
                    } as LocationDescriptor);
                }
            },
            afterSave: async () => {
                // if there is error in passengers phone numbers,
                // do not fetch data and
                // keep updated values in edit mode because save was not performed
                const phoneNumberValidationResult = validatePassengersPhoneNumbers(this.orderOperator);
                if (
                    phoneNumberValidationResult?.shouldNotSaveOrderWithInvalidPhones &&
                    phoneNumberValidationResult?.alertMessage
                ) {
                    this.orderOperator.edit(() => {});
                    return;
                }
                await this.fetchData();
            },
            validateOptions: {
                skipMissingProperties: true,
                forbidUnknownValues: false,
            },
        });

        await this.orderOperator.fetchData();

        if (this.orderOperator.newOrder) {
            this.orderOperator.addPassenger(PassengerType.Lead);

            let { userId } = this.pageRouter;
            if (!userId) {
                userId = this.authenticationStore.userJWT?.userId;
            }

            if (!userId) {
                throw new Error("User id is not defined");
            }

            if (!this.orderOperator.data.simpleUsers?.length) {
                this.orderOperator.data.simpleUsers = await this.rpcClient.user.retrieveSimpleUsers({
                    userIds: [userId],
                });
            }

            this.orderOperator.edit((o) => (o.userId = userId!));
        }
    }

    private async retrieveBlacklistedDatePriceCoefficient(model: Order): Promise<{
        priceAdjustmentCoefficient: number;
        feeAdjustmentCoefficient: number;
        vehicleCoefficients: VehicleTypeCoefficient[];
    }> {
        const priceCoefficient = await this.rpcClient.order.getOrderPriceAdjustmentCoefficient(model);

        if (
            priceCoefficient.vehicleCoefficients.length &&
            priceCoefficient.vehicleCoefficients.some(
                (coefficient) =>
                    coefficient.priceAdjustmentCoefficient !== 1 || coefficient.feeAdjustmentCoefficient !== 1,
            )
        ) {
            const confirmedBlacklistedDateCoefficient = confirm(
                `This date has average price coefficient of ${priceCoefficient.priceAdjustmentCoefficient} and average fee coefficient of ${priceCoefficient.feeAdjustmentCoefficient}, would you like to apply it to order total? Clicking OK will recalculate the price you have entered into the booking
                \nIf you click cancel, order will be created without the coefficient.`,
            );
            if (!confirmedBlacklistedDateCoefficient) {
                return {
                    priceAdjustmentCoefficient: priceCoefficient.priceAdjustmentCoefficient ?? 1,
                    feeAdjustmentCoefficient: priceCoefficient.feeAdjustmentCoefficient ?? 1,
                    vehicleCoefficients: priceCoefficient.vehicleCoefficients,
                };
            }
        }
        return { priceAdjustmentCoefficient: 1, feeAdjustmentCoefficient: 1, vehicleCoefficients: [] };
    }

    @computed
    public get poolOrderTimeRange(): string | undefined {
        const { meetingPosition, ride } = this.orderOperator.data;
        if (this.orderOperator.m.type === OrderType.Private || isUndefinedOrNull(ride)) {
            return;
        }

        const { formattedDepartureTimeText } = getPoolDepartureTimeDetails(
            {
                departureTimeEarliest: new Date(ride.departureAt).getTime() / SECOND_IN_MILLISECONDS,
                departureTimeLatest: new Date(ride.departureAtLatest).getTime() / SECOND_IN_MILLISECONDS,
                originTimezone: "UTC",
            },
            meetingPosition,
        );

        return `Pool: Departure time range for ${
            meetingPosition ? "this" : "custom"
        } pick-up position is set ${formattedDepartureTimeText}`;
    }

    public loginAsCustomer(): void {
        const email = this.orderOperator.user?.email ?? "";
        window.open(
            `${API_SIGN_IN_TO_CI_AS_CUSTOMER_URL}?email=${email}&bookingRef=${this.orderOperator.m.bookingReference}`,
            "_blank",
        );
    }

    @computed
    public get fetchHasFailed(): boolean {
        return this.orderOperator.fetchDataStatus === FetchDataStatus.Error;
    }

    public isDataFetched(): this is OrderPageStore & OrderPageStoreDataFetched {
        return isNotUndefinedOrNull(this.orderOperator);
    }

    @action
    public async cancelEdit() {
        this.orderOperator.cancelEdit();

        this.orderOperator.populatePassengersOperators();
        this.orderOperator.dynamicPriceDifference = 0;
        await this.orderOperator.retrieveVehiclesData();
        const order = await this.rpcClient.order.retrieveOrder(this.orderOperator.m._id);
        this.orderOperator.updateUiContentLocations(order);
    }

    private async loadOrderWithDependentData(operator: OrderOperator): Promise<void> {
        const order = await this.fetchOrCreateOrder(operator);

        // Operations that need order to be fetched
        await Promise.all([
            operator.retrieveVehiclesData(),
            operator.retrieveOrderPayments(),
            operator.retrieveOrdersInBundle(),
            this.fetchRouteData(order, operator),
            operator.retrieveLatestPendingChange(),
            operator.fetchMeetingPositions(),
            this.loadApiPartner(order, operator),
            this.loadUserFromOrder(order, operator),
            this.loadTravelAgentFromOrder(order, operator),
            this.loadHostAgencyFromOrder(order, operator),
            this.loadAppliedCommissionCampaigns(order, operator),
        ]);

        operator.populatePassengersOperators();

        if (order.customMarkup) {
            operator.affiliateCustomMarkup = order.customMarkup;
        }
    }

    private async fetchOrCreateOrder(operator: OrderOperator) {
        const { orderId } = this.pageRouter;

        let order: undefined | Order;
        let orderData: undefined | OrderDataForOrderPage;

        if (orderId) {
            orderData = await this.rpcClient.order.retrieveOrderDataForOrderPage(orderId);
            order = plainToClass(Order, orderData.order);
        }

        if (isNotUndefinedOrNull(order) && isNotUndefinedOrNull(orderData)) {
            operator.data = {
                ...operator.data,
                ...orderData,
                simpleUsers: [orderData.simpleCustomer],
                assignations: plainToClass(Assignation, orderData.assignations),
                locations: [...orderData.locations, orderData.originLocation, orderData.destinationLocation],
            };

            const passengersSortingMappings = {
                [PassengerType.Lead]: 1,
                [PassengerType.Adult]: 2,
                [PassengerType.Child]: 3,
            };

            order.passengers = order.passengers.sort(
                (a, b) => passengersSortingMappings[a.type] - passengersSortingMappings[b.type],
            );
        }

        if (order == null) {
            order = new Order();
            order.vehicleTypesPricesFees = VehicleTypes.map((vehicleType: VehicleType) => ({
                vehicleType,
                price: 0,
                fee: 0,
                isManuallyModified: false,
            }));
            operator.newOrder = true;
        } else {
            operator.originalVehicleTypesPricesFees = order.vehicleTypesPricesFees;
        }

        if (orderData) {
            operator.customerFeedbacks = orderData.customerFeedbacks.map((cf) => {
                const cfo = new CustomerFeedbackOperator({
                    modelConstructor: CustomerFeedback,
                    model: cf,
                    modules: null,
                    data: null,
                    onSave: async (model) => {
                        await this.rpcClient.feedback.updateCustomerFeedback(cf._id, model);
                    },
                });

                return cfo;
            });

            operator.orderDiscounts = orderData.discounts;
        }

        operator.model = order;

        operator.currency = order.currency;
        operator.currencyRate = order.currencyRate;

        return order;
    }

    private async fetchRouteData(order: Order, operator: OrderOperator) {
        if (!operator.newOrder) {
            operator.setOrderRoute({ route: operator.data.route, isInitialFetch: true });
            await operator.calculateLocationsTravelData(order.contentLocations, order.customLocations);
            operator.populateContentLocationOperators();
            operator.populateCustomLocationOperators();
            await operator.setInitialLocationArrivals();
        }
    }

    private async loadApiPartner(order: Order, operator: OrderOperator) {
        if (order.apiPartnerId) {
            try {
                const apiPartner = await this.rpcClient.apiPartner.getApiPartnerById(order.apiPartnerId);
                operator.apiPartner = apiPartner;
            } catch (e) {
                // do nothing
            }
        }
    }

    private async loadRouterUser(operator: OrderOperator) {
        if (this.pageRouter.userId != null) {
            const [user] = await this.rpcClient.user.retrieveSimpleUsers({ userIds: [this.pageRouter.userId] });
            if (operator.data.simpleUsers) {
                operator.data.simpleUsers.push(user);
            }
        }
    }

    private async loadSimpleCountries(operator: OrderOperator) {
        const countries = await this.rpcClient.content.retrieveSimpleCountries({});
        operator.data.simpleCountries = countries;
    }

    private async loadRegions(operator: OrderOperator) {
        operator.data.regions = await this.rpcClient.content.retrieveRegions({});
    }

    private async loadHostAgencies(operator: OrderOperator) {
        const hostAgencies = await this.rpcClient.hostAgency.retrieveHostAgencies();
        operator.data.hostAgencies = hostAgencies;
    }

    private async loadAppliedCommissionCampaigns(order: Order, operator: OrderOperator) {
        const campaignIds = order.commission?.appliedCommissions?.map((c) => c.campaignId) || [];
        if (campaignIds.length === 0) {
            operator.appliedCommissionCampaigns = [];
            return;
        }

        operator.appliedCommissionCampaigns =
            await this.rpcClient.commissionCampaign.getCommissionCampaignsByIds(campaignIds);
    }

    // ts_fix_bullshit Fix OrderOperator type and use here
    private async loadHostAgencyFromOrder(order: Order, operator: any) {
        if (!order.hostAgencyId) {
            return;
        }

        const hostAgency = await this.rpcClient.hostAgency.retrieveHostAgency(order.hostAgencyId);

        if (hostAgency) {
            operator.hostAgencyId = hostAgency._id;
        }
    }

    private async loadUserFromOrder(order: Order, operator: OrderOperator) {
        if (!order.userId || this.authenticationStore.isRegionalManager) {
            return;
        }
        const userFromOrder = await this.rpcClient.user.retrieveUserByIdIncludingDeleted(order.userId);
        operator.data.userFromOrder = userFromOrder;
    }

    // ts_fix_bullshit Fix OrderOperator type and use here
    private async loadTravelAgentFromOrder(order: Order, operator: any) {
        if (!order.travelAgentId) {
            return;
        }

        const travelAgents = await apiTrpcClient.travelAgent.retrieveTravelAgents.query({
            ids: [order.travelAgentId],
        });

        if (travelAgents?.length > 0) {
            const travelAgent = travelAgents[0];
            operator.travelAgentId = travelAgent.userId;
            operator.hostAgencyId = travelAgent.hostAgencyId;

            const simpleUser = {
                _id: travelAgent.userId,
                fullName: travelAgent.fullName,
                email: travelAgent.email,
                isTravelAgent: true,
                travelAgent: {
                    hostAgencyId: travelAgent.hostAgencyId,
                },
            };

            operator.data.simpleUsers = [...(operator.data.simpleUsers ?? []), simpleUser];
        }
    }

    private getTripType(order: Order): string | undefined {
        if (order.offerType === OfferType.DayTrip) {
            return "dayTrip";
        }

        if (order.bundleId) {
            // For bundles, we would need to check bundle type
            // to determine returnTrip vs multiCity
            // For now, return bundle
            return "bundle";
        }

        return "oneWay";
    }
}

export interface OrderPageStoreDataFetched {
    orderOperator: OrderOperator;
}
